/**
 * The hover layer, as a primitive.
 *
 * Two things it insists on. It opens on **focus as well as hover**, so every value a
 * tooltip carries is reachable from the keyboard - a chart whose numbers only exist
 * under a pointer has hidden them from anyone not using one. And it never *gates* a
 * value: everything shown here is also in the table view, so the tooltip enhances
 * rather than holds the only copy.
 *
 * Positioning is deliberately hand-rolled and flip-only. A floating-ui dependency
 * would buy collision handling this does not need: the tooltips here sit in a grid of
 * cards, and clamping to the viewport with a vertical flip covers every case.
 */

import { useCallback, useId, useLayoutEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'

import { cn } from '../../lib/cn'

interface TooltipProps {
  /** Tooltip body. Plain nodes - callers pass text, never markup from the API. */
  content: ReactNode
  children: ReactNode
  /** Wrapper element. `svg` keeps SVG marks valid, where a `div` would not be. */
  as?: 'span' | 'div' | 'g'
  className?: string
  /**
   * Applied to the wrapper, not to `children`. A sized mark - a stacked-bar segment,
   * say - must carry its width on the element that is the flex child, which is this
   * wrapper; putting it on an inner div collapses the segment to zero.
   */
  style?: React.CSSProperties
  /** Skip rendering the layer entirely, for a mark with nothing to add. */
  disabled?: boolean
}

export function Tooltip({
  content,
  children,
  as = 'span',
  className,
  style,
  disabled,
}: TooltipProps) {
  const [open, setOpen] = useState(false)
  const [placement, setPlacement] = useState<'top' | 'bottom'>('top')
  const anchorRef = useRef<HTMLElement | SVGGElement | null>(null)
  const bubbleRef = useRef<HTMLDivElement | null>(null)
  const id = useId()

  const show = useCallback(() => setOpen(true), [])
  const hide = useCallback(() => setOpen(false), [])

  useLayoutEffect(() => {
    if (!open || !anchorRef.current || !bubbleRef.current) return
    const anchor = anchorRef.current.getBoundingClientRect()
    const bubble = bubbleRef.current.getBoundingClientRect()

    // Flip below when there is not room above, and clamp horizontally so a tooltip
    // on the leftmost or rightmost mark stays on screen.
    setPlacement(anchor.top - bubble.height - 10 < 0 ? 'bottom' : 'top')

    const half = bubble.width / 2
    const centre = anchor.left + anchor.width / 2
    const clamped = Math.min(Math.max(centre, half + 8), window.innerWidth - half - 8)
    bubbleRef.current.style.left = `${clamped - centre}px`
  }, [open, content])

  if (disabled) return <>{children}</>

  const bubble = open ? (
    <div
      ref={bubbleRef}
      role="tooltip"
      id={id}
      className={cn(
        'pointer-events-none absolute left-1/2 z-50 w-max max-w-[19rem] -translate-x-1/2',
        'card px-2.5 py-2 text-xs leading-relaxed shadow-lg',
        placement === 'top' ? 'bottom-full mb-2' : 'top-full mt-2',
      )}
    >
      {content}
    </div>
  ) : null

  const handlers = {
    onPointerEnter: show,
    onPointerLeave: hide,
    onFocus: show,
    onBlur: hide,
    // Escape closes without moving the pointer, for keyboard users.
    onKeyDown: (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') hide()
    },
  }

  if (as === 'g') {
    return (
      <g
        ref={anchorRef as React.Ref<SVGGElement>}
        tabIndex={0}
        aria-describedby={open ? id : undefined}
        className={cn('outline-none', className)}
        {...handlers}
      >
        {children}
        {open ? (
          <foreignObject x={0} y={0} width={1} height={1} className="overflow-visible">
            <div className="relative">{bubble}</div>
          </foreignObject>
        ) : null}
      </g>
    )
  }

  const Wrapper = as
  return (
    <Wrapper
      ref={anchorRef as React.Ref<HTMLElement & HTMLDivElement & HTMLSpanElement>}
      tabIndex={0}
      aria-describedby={open ? id : undefined}
      className={cn('relative outline-none', className)}
      style={style}
      {...handlers}
    >
      {children}
      {bubble}
    </Wrapper>
  )
}

/**
 * A tooltip row: value first in strong ink, label second in secondary.
 *
 * The legend's hierarchy inverted on purpose - here the reader already knows which
 * series they are pointing at and wants the number.
 */
export function TooltipRow({
  value,
  label,
  swatch,
}: {
  value: ReactNode
  label: ReactNode
  /** A short stroke of the series colour, not a filled box - a label's job, not data's. */
  swatch?: string
}) {
  return (
    <div className="flex items-baseline gap-2">
      {swatch ? (
        <span
          aria-hidden
          className="mt-1.5 inline-block h-0.5 w-3 shrink-0 rounded-full"
          style={{ backgroundColor: swatch }}
        />
      ) : null}
      <span className="tabular font-semibold text-ink">{value}</span>
      <span className="text-ink-secondary">{label}</span>
    </div>
  )
}
